import { useState, useEffect, useCallback } from 'react';
import { toast } from 'sonner';
import { MainLayout } from '../../components/MainLayout';
import api from '../../lib/api';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Card } from '../../components/ui/card';
import { Tabs, TabsList, TabsTrigger } from '../../components/ui/tabs';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '../../components/ui/table';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../../components/ui/select';
import {
  Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription,
} from '../../components/ui/sheet';
import {
  TooltipProvider, Tooltip, TooltipTrigger, TooltipContent,
} from '../../components/ui/tooltip';
import { Skeleton } from '../../components/ui/skeleton';
import { Checkbox } from '../../components/ui/checkbox';
import {
  MailWarning, RefreshCw, Download, Loader2, Info, MessageSquare,
  CheckCircle2, Clock3, Send, FileText,
} from 'lucide-react';

const fmtDate = (v) => {
  if (!v) return '\u2014';
  const d = new Date(v);
  return isNaN(d) ? v : d.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: '2-digit' });
};

const CADENCE_LABEL = {
  monthly: 'Monthly', quarterly: 'Quarterly', need_based: 'Need-based', unknown: 'Unknown',
};

const cadenceReason = (r) => {
  const label = CADENCE_LABEL[r.inferred_cadence] || r.inferred_cadence;
  const pd = r.period_days;
  if (r.inferred_cadence === 'need_based') {
    return 'Need-based — no billing period on bills and no regular arrival pattern, so it is never auto-flagged as Missing.';
  }
  switch (r.cadence_source) {
    case 'frequency':
      return `${label} — from the account's declared billing frequency.`;
    case 'period':
      return `${label} — derived from the bill's billing period (~${pd}-day cycle).`;
    case 'inferred':
      return `${label} — inferred from the pattern of past bill arrival dates (~${pd}-day cycle).`;
    default:
      return `${label} cadence (${pd}-day cycle).`;
  }
};

function DaysBadge({ days, severity }) {
  if (!days) return <span className="text-muted-foreground">0</span>;
  const cls = severity === 'red'
    ? 'bg-red-100 text-red-700 border-red-200'
    : severity === 'amber'
      ? 'bg-amber-100 text-amber-700 border-amber-200'
      : 'bg-muted text-muted-foreground';
  return <Badge variant="outline" className={cls}>{days}d</Badge>;
}

export default function MissingBills() {
  const [currentAccount] = useState(() => {
    const s = localStorage.getItem('current_account');
    return s ? JSON.parse(s) : null;
  });
  const accountId = currentAccount?.id;

  const [tab, setTab] = useState('late_arriving');
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [recomputing, setRecomputing] = useState(false);
  const [users, setUsers] = useState([]);

  // global filters
  const [opts, setOpts] = useState({ vendors: [], account_numbers: [], properties: [] });
  const [fVendor, setFVendor] = useState('all');
  const [fAccount, setFAccount] = useState('all');
  const [fProperty, setFProperty] = useState('all');
  const [fSeverity, setFSeverity] = useState('all');
  const [fAssigned, setFAssigned] = useState('all');
  const [showSnoozed, setShowSnoozed] = useState(false);

  // notes drawer
  const [notesOpen, setNotesOpen] = useState(false);
  const [notesRow, setNotesRow] = useState(null);
  const [notes, setNotes] = useState([]);
  const [noteBody, setNoteBody] = useState('');
  const [savingNote, setSavingNote] = useState(false);

  // bill PDF drawer (slides from left)
  const [billOpen, setBillOpen] = useState(false);
  const [billRow, setBillRow] = useState(null);
  const [billPdf, setBillPdf] = useState(null);
  const [billUrl, setBillUrl] = useState(null);
  const [billLoading, setBillLoading] = useState(false);

  const PAGE_SIZE = 50;

  const listParams = useCallback((offset) => ({
    account_id: accountId, flag: tab,
    vendor_name: fVendor !== 'all' ? fVendor : undefined,
    account_number: fAccount !== 'all' ? fAccount : undefined,
    property_id: fProperty !== 'all' ? fProperty : undefined,
    severity: fSeverity !== 'all' ? fSeverity : undefined,
    assigned_to: fAssigned !== 'all' ? fAssigned : undefined,
    show_snoozed: showSnoozed, limit: PAGE_SIZE, offset,
  }), [accountId, tab, fVendor, fAccount, fProperty, fSeverity, fAssigned, showSnoozed]);

  const load = useCallback(async () => {
    if (!accountId) return;
    setLoading(true);
    try {
      const { data } = await api.get('/intake/missing-bills/list', { params: listParams(0) });
      setRows(data.items || []);
      setTotal(data.total || 0);
    } catch {
      toast.error('Failed to load missing bills');
    } finally {
      setLoading(false);
    }
  }, [accountId, listParams]);

  useEffect(() => { load(); }, [load]);

  const loadStats = useCallback(() => {
    if (!accountId) return;
    api.get('/intake/missing-bills/stats', { params: { account_id: accountId } })
      .then(({ data }) => setStats(data)).catch(() => {});
  }, [accountId]);

  useEffect(() => { loadStats(); }, [loadStats]);

  useEffect(() => {
    if (!accountId) return;
    api.get('/intake/missing-bills/assignable-users', { params: { account_id: accountId } })
      .then(({ data }) => setUsers(data.users || [])).catch(() => {});
    api.get('/intake/missing-bills/filter-options', { params: { account_id: accountId } })
      .then(({ data }) => setOpts(data || { vendors: [], account_numbers: [], properties: [] }))
      .catch(() => {});
  }, [accountId]);

  const loadMore = async () => {
    setLoadingMore(true);
    try {
      const { data } = await api.get('/intake/missing-bills/list', { params: listParams(rows.length) });
      setRows((prev) => [...prev, ...(data.items || [])]);
      setTotal(data.total || 0);
    } catch {
      toast.error('Failed to load more');
    } finally {
      setLoadingMore(false);
    }
  };

  const refreshOptions = () =>
    api.get('/intake/missing-bills/filter-options', { params: { account_id: accountId } })
      .then(({ data }) => setOpts(data)).catch(() => {});

  const recompute = async () => {
    setRecomputing(true);
    try {
      const { data } = await api.post('/intake/missing-bills/recompute', {}, { params: { account_id: accountId } });
      if (data.skipped_no_config) {
        toast.info('No workflow config for this org — forecasting skipped (deliberate cutover).');
      } else {
        toast.success(`Recomputed · ${data.late_arriving} late · ${data.missing} missing · ${data.units_marked_occupied} units→occupied · ${data.accounts_ignored} ignored`);
      }
      await load(); await refreshOptions(); loadStats();
    } catch {
      toast.error('Recompute failed');
    } finally {
      setRecomputing(false);
    }
  };

  const assign = async (row, userId) => {
    try {
      const { data } = await api.patch(`/intake/missing-bills/${row.id}/assign`,
        { assigned_to: userId === 'unassigned' ? null : userId },
        { params: { account_id: accountId } });
      setRows((prev) => prev.map((r) => (r.id === row.id ? data : r)));
      loadStats();
    } catch {
      toast.error('Assignment failed');
    }
  };

  const snooze = async (row) => {
    try {
      await api.post(`/intake/missing-bills/${row.id}/snooze`, {}, { params: { account_id: accountId } });
      toast.success('Snoozed 7 days');
      await load(); loadStats();
    } catch { toast.error('Snooze failed'); }
  };

  const markReceived = async (row) => {
    try {
      await api.post(`/intake/missing-bills/${row.id}/mark-received`, {}, { params: { account_id: accountId } });
      toast.success('Marked received & archived');
      await load(); loadStats();
    } catch { toast.error('Mark received failed'); }
  };

  const openNotes = async (row) => {
    setNotesRow(row); setNotesOpen(true); setNotes([]); setNoteBody('');
    try {
      const { data } = await api.get(`/intake/missing-bills/${row.id}/notes`, { params: { account_id: accountId } });
      setNotes(data.notes || []);
    } catch { toast.error('Failed to load notes'); }
  };

  const addNote = async () => {
    if (!noteBody.trim()) return;
    setSavingNote(true);
    try {
      const { data } = await api.post(`/intake/missing-bills/${notesRow.id}/notes`,
        { body: noteBody.trim() }, { params: { account_id: accountId } });
      setNotes((prev) => [data, ...prev]);
      setNoteBody('');
      setRows((prev) => prev.map((r) => (r.id === notesRow.id
        ? { ...r, notes_count: (r.notes_count || 0) + 1 } : r)));
    } catch { toast.error('Failed to add note'); }
    finally { setSavingNote(false); }
  };

  const openBill = async (row) => {
    if (!row.latest_bill_id) { toast.info('No bill linked'); return; }
    setBillRow(row); setBillOpen(true); setBillPdf(null); setBillUrl(null); setBillLoading(true);
    try {
      const { data } = await api.get(`/payments/v2/bill-pdf/${row.latest_bill_id}`,
        { params: { account_id: accountId } });
      if (data?.base64_data) setBillPdf(data.base64_data);
      else if (data?.pdf_url) setBillUrl(data.pdf_url);
    } catch {
      setBillPdf(null); setBillUrl(null);
    } finally { setBillLoading(false); }
  };

  const downloadCsv = async () => {
    try {
      const { data } = await api.get('/intake/missing-bills/export.csv', {
        params: { ...listParams(0), limit: undefined, offset: undefined }, responseType: 'blob',
      });
      const url = window.URL.createObjectURL(new Blob([data]));
      const a = document.createElement('a');
      a.href = url; a.download = `missing_bills_${tab}.csv`; a.click();
      window.URL.revokeObjectURL(url);
    } catch { toast.error('Download failed'); }
  };

  const billPdfUrl = billPdf ? `data:application/pdf;base64,${billPdf}` : (billUrl || null);

  return (
    <MainLayout>
      <TooltipProvider>
        <div className="space-y-5" data-testid="missing-bills-page">
          <div className="flex items-center justify-between flex-wrap gap-3">
            <div>
              <h1 className="text-2xl font-semibold flex items-center gap-2">
                <MailWarning className="h-6 w-6 text-primary" /> Missing Bills
              </h1>
              <p className="text-sm text-muted-foreground mt-1">
                Each missing billing period is tracked as its own row. Units with no bill for 45+ days auto-flip to occupied; stale series (90+ days) are auto-ignored.
              </p>
            </div>
            <div className="flex gap-2">
              <Button variant="outline" size="sm" onClick={downloadCsv}
                data-testid={`mb-csv-download-${tab === 'late_arriving' ? 'late' : 'missing'}`}>
                <Download className="h-4 w-4 mr-1" /> CSV
              </Button>
              <Button size="sm" onClick={recompute} disabled={recomputing} data-testid="mb-recompute-btn">
                {recomputing ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <RefreshCw className="h-4 w-4 mr-1" />}
                Recompute
              </Button>
            </div>
          </div>

          {/* Top-level stats */}
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3" data-testid="mb-stats">
            <Card
              className={`p-4 cursor-pointer transition-colors ${tab === 'late_arriving' ? 'border-primary ring-1 ring-primary/30' : 'hover:border-primary/40'}`}
              onClick={() => setTab('late_arriving')}
              data-testid="mb-stat-late-arriving"
            >
              <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                <Clock3 className="h-3.5 w-3.5 text-amber-500" /> Late Arriving
              </div>
              <div className="mt-1 text-2xl font-bold" data-testid="mb-stat-late-arriving-count">
                {stats ? stats.late_arriving.total : '—'}
              </div>
              {stats && (stats.late_arriving.red > 0 || stats.late_arriving.amber > 0) && (
                <div className="mt-1.5 flex gap-1.5">
                  <Badge variant="outline" className="border-rose-200 bg-rose-50 text-rose-700 text-[10px]">{stats.late_arriving.red} red</Badge>
                  <Badge variant="outline" className="border-amber-200 bg-amber-50 text-amber-700 text-[10px]">{stats.late_arriving.amber} amber</Badge>
                </div>
              )}
            </Card>

            <Card
              className={`p-4 cursor-pointer transition-colors ${tab === 'missing' ? 'border-primary ring-1 ring-primary/30' : 'hover:border-primary/40'}`}
              onClick={() => setTab('missing')}
              data-testid="mb-stat-missing"
            >
              <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                <MailWarning className="h-3.5 w-3.5 text-rose-500" /> Missing
              </div>
              <div className="mt-1 text-2xl font-bold" data-testid="mb-stat-missing-count">
                {stats ? stats.missing.total : '—'}
              </div>
              {stats && (stats.missing.red > 0 || stats.missing.amber > 0) && (
                <div className="mt-1.5 flex gap-1.5">
                  <Badge variant="outline" className="border-rose-200 bg-rose-50 text-rose-700 text-[10px]">{stats.missing.red} red</Badge>
                  <Badge variant="outline" className="border-amber-200 bg-amber-50 text-amber-700 text-[10px]">{stats.missing.amber} amber</Badge>
                </div>
              )}
            </Card>

            <Card className="p-4" data-testid="mb-stat-total">
              <div className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Total Open</div>
              <div className="mt-1 text-2xl font-bold" data-testid="mb-stat-total-count">{stats ? stats.total : '—'}</div>
            </Card>

            <Card className="p-4" data-testid="mb-stat-assigned">
              <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                <CheckCircle2 className="h-3.5 w-3.5 text-emerald-500" /> Assigned
              </div>
              <div className="mt-1 text-2xl font-bold" data-testid="mb-stat-assigned-count">{stats ? stats.assigned : '—'}</div>
            </Card>

            <Card className="p-4" data-testid="mb-stat-unassigned">
              <div className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Unassigned</div>
              <div className="mt-1 text-2xl font-bold" data-testid="mb-stat-unassigned-count">{stats ? stats.unassigned : '—'}</div>
            </Card>

            <Card className="p-4" data-testid="mb-stat-archived">
              <div className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Archived</div>
              <div className="mt-1 text-2xl font-bold" data-testid="mb-stat-archived-count">{stats ? stats.archived : '—'}</div>
            </Card>
          </div>

          <Tabs value={tab} onValueChange={setTab}>
            <TabsList>
              <TabsTrigger value="late_arriving" data-testid="missing-bills-tab-late">Late Arriving Bills</TabsTrigger>
              <TabsTrigger value="missing" data-testid="missing-bills-tab-missing">Missing Bills</TabsTrigger>
            </TabsList>
          </Tabs>

          {/* Global filters (server-side, apply across all pages) */}
          <div className="flex flex-wrap items-center gap-3" data-testid="mb-filters">
            <Select value={fProperty} onValueChange={setFProperty}>
              <SelectTrigger className="w-48" data-testid="mb-filter-property"><SelectValue placeholder="Property" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All properties</SelectItem>
                {opts.properties.map((p) => <SelectItem key={p.property_id} value={p.property_id}>{p.property_name}</SelectItem>)}
              </SelectContent>
            </Select>
            <Select value={fVendor} onValueChange={setFVendor}>
              <SelectTrigger className="w-44" data-testid="mb-filter-vendor"><SelectValue placeholder="Vendor" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All vendors</SelectItem>
                {opts.vendors.map((v) => <SelectItem key={v} value={v}>{v}</SelectItem>)}
              </SelectContent>
            </Select>
            <Select value={fAccount} onValueChange={setFAccount}>
              <SelectTrigger className="w-44" data-testid="mb-filter-account"><SelectValue placeholder="Account #" /></SelectTrigger>
              <SelectContent className="max-h-72">
                <SelectItem value="all">All accounts</SelectItem>
                {opts.account_numbers.map((a) => <SelectItem key={a} value={a}>{a}</SelectItem>)}
              </SelectContent>
            </Select>
            <Select value={fSeverity} onValueChange={setFSeverity}>
              <SelectTrigger className="w-36" data-testid="mb-filter-severity"><SelectValue placeholder="Severity" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Any severity</SelectItem>
                <SelectItem value="red">Red (&gt;4d)</SelectItem>
                <SelectItem value="amber">Amber (&gt;3d)</SelectItem>
              </SelectContent>
            </Select>
            <Select value={fAssigned} onValueChange={setFAssigned}>
              <SelectTrigger className="w-44" data-testid="mb-filter-assigned"><SelectValue placeholder="Assignee" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All assignees</SelectItem>
                {users.map((u) => <SelectItem key={u.user_id} value={u.user_id}>{u.name}</SelectItem>)}
              </SelectContent>
            </Select>
            <label className="flex items-center gap-2 text-sm cursor-pointer" data-testid="mb-show-snoozed">
              <Checkbox checked={showSnoozed} onCheckedChange={(v) => setShowSnoozed(!!v)} /> Show snoozed
            </label>
          </div>

          <Card className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Property</TableHead>
                  <TableHead>Unit / Address</TableHead>
                  <TableHead>Vendor</TableHead>
                  <TableHead>Account #</TableHead>
                  <TableHead>
                    <span className="inline-flex items-center gap-1">
                      Latest Bill
                      <Tooltip>
                        <TooltipTrigger asChild><Info className="h-3 w-3 text-muted-foreground" /></TooltipTrigger>
                        <TooltipContent>Latest received bill issue date for the account &amp; vendor. Click the icon to view the PDF.</TooltipContent>
                      </Tooltip>
                    </span>
                  </TableHead>
                  <TableHead>Expected</TableHead>
                  <TableHead>Days</TableHead>
                  <TableHead>Cadence</TableHead>
                  <TableHead>Assigned To</TableHead>
                  <TableHead>Notes</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {loading ? (
                  [1, 2, 3].map((i) => (
                    <TableRow key={i}><TableCell colSpan={11}><Skeleton className="h-6 w-full" /></TableCell></TableRow>
                  ))
                ) : rows.length === 0 ? (
                  <TableRow><TableCell colSpan={11} className="text-center text-muted-foreground py-8">
                    Nothing here. Click <strong>Recompute</strong> to refresh forecasts.
                  </TableCell></TableRow>
                ) : rows.map((r) => (
                  <TableRow key={r.id} data-testid={`mb-row-${r.id}`}>
                    <TableCell className="font-medium">{r.property_name || '\u2014'}</TableCell>
                    <TableCell className="text-sm text-muted-foreground">
                      {r.unit_number ? <span className="font-medium text-foreground">Unit {r.unit_number}</span> : null}
                      {r.unit_number && r.service_address ? ' · ' : ''}
                      {r.service_address || (!r.unit_number ? '\u2014' : '')}
                    </TableCell>
                    <TableCell>{r.vendor_name}</TableCell>
                    <TableCell>{r.vendor_account_number}</TableCell>
                    <TableCell className="text-sm">
                      <span className="inline-flex items-center gap-1.5">
                        {fmtDate(r.latest_bill_issue_date)}
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <button onClick={() => openBill(r)} className="text-primary hover:text-primary/70 transition-colors"
                              data-testid={`mb-bill-icon-${r.id}`}>
                              <FileText className="h-4 w-4" />
                            </button>
                          </TooltipTrigger>
                          <TooltipContent>View latest bill PDF</TooltipContent>
                        </Tooltip>
                      </span>
                    </TableCell>
                    <TableCell className="text-sm">{fmtDate(r.expected_issue_date)}</TableCell>
                    <TableCell><DaysBadge days={r.days_past_expected} severity={r.severity} /></TableCell>
                    <TableCell>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <Badge variant="secondary" className="cursor-help" data-testid={`mb-cadence-${r.id}`}>
                            {CADENCE_LABEL[r.inferred_cadence] || r.inferred_cadence}
                          </Badge>
                        </TooltipTrigger>
                        <TooltipContent className="max-w-xs">{cadenceReason(r)}</TooltipContent>
                      </Tooltip>
                    </TableCell>
                    <TableCell>
                      <Select value={r.assigned_to || 'unassigned'} onValueChange={(v) => assign(r, v)}>
                        <SelectTrigger className="w-40 h-8" data-testid={`mb-assign-${r.id}`}>
                          <SelectValue placeholder="Unassigned" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="unassigned">Unassigned</SelectItem>
                          {users.map((u) => <SelectItem key={u.user_id} value={u.user_id}>{u.name}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </TableCell>
                    <TableCell>
                      <Button variant="ghost" size="sm" onClick={() => openNotes(r)} data-testid={`mb-notes-${r.id}`}>
                        <MessageSquare className="h-4 w-4 mr-1" /> {r.notes_count || 0}
                      </Button>
                    </TableCell>
                    <TableCell className="text-right whitespace-nowrap">
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <Button variant="ghost" size="sm" onClick={() => snooze(r)} data-testid={`mb-snooze-${r.id}`}>
                            <Clock3 className="h-4 w-4" />
                          </Button>
                        </TooltipTrigger>
                        <TooltipContent>Snooze 7 days</TooltipContent>
                      </Tooltip>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <Button variant="ghost" size="sm" onClick={() => markReceived(r)} data-testid={`mb-mark-received-${r.id}`}>
                            <CheckCircle2 className="h-4 w-4 text-emerald-600" />
                          </Button>
                        </TooltipTrigger>
                        <TooltipContent>Mark received (archive)</TooltipContent>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Card>

          {!loading && rows.length > 0 && (
            <div className="flex items-center justify-between text-sm text-muted-foreground" data-testid="mb-pagination">
              <span>Showing {rows.length} of {total}</span>
              {rows.length < total && (
                <Button variant="outline" size="sm" onClick={loadMore} disabled={loadingMore} data-testid="mb-load-more">
                  {loadingMore ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : null}
                  Load more ({total - rows.length} left)
                </Button>
              )}
            </div>
          )}
        </div>

        {/* Notes drawer (right) */}
        <Sheet open={notesOpen} onOpenChange={setNotesOpen}>
          <SheetContent className="w-full sm:max-w-md flex flex-col" data-testid="mb-notes-drawer">
            <SheetHeader>
              <SheetTitle>Notes — {notesRow?.vendor_name} · {notesRow?.vendor_account_number}</SheetTitle>
              <SheetDescription>Append-only notes for this missing-bill period.</SheetDescription>
            </SheetHeader>
            <div className="flex-1 overflow-y-auto space-y-3 py-4">
              {notes.length === 0 ? (
                <p className="text-sm text-muted-foreground">No notes yet.</p>
              ) : notes.map((n) => (
                <div key={n.id} className="rounded-md border p-3" data-testid={`mb-note-${n.id}`}>
                  <div className="text-sm">{n.body}</div>
                  <div className="text-xs text-muted-foreground mt-1">{n.author_name || 'Unknown'} · {fmtDate(n.created_at)}</div>
                </div>
              ))}
            </div>
            <div className="border-t pt-3 space-y-2">
              <textarea className="w-full border rounded-md p-2 text-sm min-h-[80px]"
                placeholder="Add a note (append-only)…" value={noteBody}
                onChange={(e) => setNoteBody(e.target.value)} data-testid="mb-note-input" />
              <Button size="sm" className="w-full" onClick={addNote} disabled={savingNote || !noteBody.trim()} data-testid="mb-note-save">
                {savingNote ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Send className="h-4 w-4 mr-1" />}
                Add Note
              </Button>
            </div>
          </SheetContent>
        </Sheet>

        {/* Bill PDF drawer (slides from LEFT) */}
        <Sheet open={billOpen} onOpenChange={setBillOpen}>
          <SheetContent side="left" className="w-full sm:max-w-2xl flex flex-col" data-testid="mb-bill-drawer">
            <SheetHeader>
              <SheetTitle>Latest Bill — {billRow?.vendor_name} · {billRow?.vendor_account_number}</SheetTitle>
              <SheetDescription>PDF of the most recent received bill for this account.</SheetDescription>
            </SheetHeader>
            <div className="flex-1 overflow-hidden py-3">
              {billLoading ? (
                <div className="flex items-center gap-2 text-muted-foreground justify-center h-full">
                  <Loader2 className="h-5 w-5 animate-spin" /> Loading bill…
                </div>
              ) : billPdfUrl ? (
                <iframe title="bill-pdf" src={billPdfUrl} className="w-full h-full rounded-md border" data-testid="mb-bill-iframe" />
              ) : (
                <p className="text-sm text-muted-foreground text-center mt-8">No PDF available for this bill.</p>
              )}
            </div>
          </SheetContent>
        </Sheet>
      </TooltipProvider>
    </MainLayout>
  );
}
